import React, { useState } from "react";

const experienceData = [
  {
    id: 0,
    role: "Software AI/ML Engineer",
    company: "AURA",
    badge: "images/AURA-2.png",
    companyUrl: "https://www.aurasos.com",
    period: "Nov 2019 - Present",
    sections: [
      {
        title: "Technical Responsibilities",
        items: [
          "Designed and built scalable, high-availability backend systems supporting real-time operations and high-traffic workloads.",
          "Led CI/CD pipeline implementation, improving deployment speed and release reliability across production environments.",
          "Introduced automated testing frameworks, increasing code coverage and reducing production defects.",
          "Optimized complex PostgreSQL queries, improving database performance and reducing API response times.",
          "Migrated legacy monolithic systems to a microservices architecture, enhancing scalability, modularity, and maintainability.",
          "Mentored junior engineers and conducted code reviews, improving code quality and enforcing best engineering practices.",
        ],
      },
      {
        title: "Machine Learning & AI Engineering",
        items: [
          "Implemented ML models to predict responder behavior, including likelihood of callout acceptance, decline, and stand-down scenarios.",
          "Engineered features from real-world operational data to improve model accuracy and decision-making.",
          "Integrated predictive models into backend systems to enable intelligent dispatch and operational optimization.",
          "Built AI-driven agents to automate backend workflows, including endpoint generation, decision support, and system orchestration.",
          "Developed data preprocessing and transformation pipelines using Python and Pandas to prepare datasets for model training and inference.",
          "Designed and implemented OpenAI GPT integration for speech processing, enabling intelligent, scalable, and production-ready workflows.",
        ],
      },
      {
        title: "Leadership & Collaboration",
        items: [
          "Facilitated daily stand-up meetings throughout the sprint cycle, ensuring team alignment, tracking progress, and proactively resolving blockers.",
          "Led end-to-end system testing for major releases, ensuring all core functionalities were validated across the platform before deployment.",
          "Owned production release processes, leading deployments and ensuring services were successfully shipped without disrupting operations across multiple regions.",
        ],
      },
    ],
  },
  {
    id: 1,
    role: "Software Developer",
    company: "ORNICO-GROUP",
    badge: "images/ornico.png",
    companyUrl: "https://ornico.co/",
    period: "Feb 2018 — Oct 2019",
    sections: [
      {
        title: "Technical Responsibilities",
        items: [
          "Developed and maintained clean, efficient code per requirements.",
          "Executed unit and integration testing to ensure product quality.",
          "Utilized Git for version control and collaboration.",
          "Engaged in cross-functional planning and requirement analysis with stakeholders.",
          "Delivered solutions through Agile collaboration with designers, QA, and product managers."
        ],
      }
    ],
  },
  {
    id: 2,
    role: "Software Developer Intern",
    company: "ORANGE DRC",
    badge: "images/orange.png",
    companyUrl: "https://www.orange.cd/",
    period: "Feb 2013 — Sept 2013",
    sections: [
      {
        title: "Technical Responsibilities",
        items: [
          "Developed software applications, participating in end-to-end SDLC.",
          "Assisted with debugging and documentation for key applications.",
          "Collaborated with engineering teams to achieve project objectives.",
          "Conducted code reviews to ensure quality standards.",
          "Engaged in testing processes to validate software functionality."
        ],
      }
    ],
  },
];

const Experience = () => {
  const [openSections, setOpenSections] = useState({});

  const toggleSection = (sectionKey) => {
    setOpenSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  };

  return (
    <div className="min-h-screen pt-24 pb-16 bg-gradient-to-br from-slate-900 via-purple-900/20 to-slate-900 relative">
      <div className="container mx-auto px-8">
        <div className="text-center mb-16">
          <h2 className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-cyan-400 via-purple-400 to-emerald-400 bg-clip-text text-transparent mb-4">
            Professional Experience
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            A journey through my career milestones
          </p>
        </div>

        {/* Professional Grid Layout */}
        <div className="grid grid-cols-1 gap-8 max-w-7xl mx-auto">
          {experienceData.map((exp, index) => (
            <div key={exp.id} className="group relative">
              <div className="bg-gradient-to-br from-gray-800/90 to-gray-900/90 backdrop-blur-lg border border-gray-700/50 rounded-2xl shadow-xl p-6 h-full transform transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:border-cyan-500/50">
                {/* Status Badge */}
                <div className="absolute -top-3 -right-3">
                  <div className="w-6 h-6 bg-gradient-to-r from-yellow-400 via-orange-400 to-red-400 rounded-full border-2 border-slate-900 flex items-center justify-center">
                    <span className="text-xs font-bold text-slate-900">
                      {index + 1}
                    </span>
                  </div>
                </div>

                {/* Company Badge/Icon */}
                <div className="flex items-stretch mb-8">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-cyan-500/20 to-purple-500/20 border border-cyan-500/30 flex items-center justify-center mr-4 shrink-0 group-hover:border-cyan-400/50 transition-colors">
                    {exp.badge ? (
                      <img
                        src={exp.badge}
                        alt={`${exp.company} certificate`}
                        className="w-full h-full object-cover rounded-xl"
                        onError={(e) => {
                          e.target.style.display = "none";
                          e.target.nextSibling.style.display = "flex";
                        }}
                      />
                    ) : null}
                    <svg
                      className={exp.badge ? "hidden" : "w-7 h-7 text-cyan-400"}
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                    </svg>
                  </div>
                  <div className="flex-1 h-14 text-left flex flex-col justify-between">
                    <h3 className="text-2xl font-bold text-white leading-tight">
                      {exp.company}
                    </h3>
                    <p className="text-lg text-emerald-400 font-semibold leading-tight">
                      {exp.role}
                    </p>
                  </div>
                </div>

                {/* Experience Details */}
                <div className="relative pl-20 mb-8 text-left">
                  <div className="absolute left-8 top-1 bottom-1 w-px bg-gradient-to-b from-cyan-400 via-slate-400 to-emerald-400" />
                  <div className="absolute left-8 top-2 w-3 h-3 -translate-x-[5px] rounded-full border-2 border-cyan-300 bg-slate-950 shadow-[0_0_0_4px_rgba(34,211,238,0.12)]" />
                  <div className="space-y-8">
                    {exp.sections.map((section) => {
                      const sectionKey = `${exp.id}-${section.title}`;
                      const isOpen = openSections[sectionKey];

                      return (
                        <div key={section.title}>
                          <div className="flex items-center gap-3 mb-4">
                            <h4 className="text-gray-300 text-base font-semibold leading-relaxed">
                              {section.title}:
                            </h4>
                            <button
                              type="button"
                              onClick={() => toggleSection(sectionKey)}
                              className="text-xs font-medium text-cyan-300 hover:text-cyan-200 transition-colors"
                            >
                              {isOpen ? "Hide" : "Show"}
                            </button>
                          </div>
                          {isOpen && (
                            <ul className="space-y-3 text-gray-300 text-base leading-relaxed">
                              {section.items.map((item) => (
                                <li key={item} className="flex items-start gap-3">
                                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-cyan-300" />
                                  <span>{item}</span>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Period and Certification */}
                <div className="flex items-center justify-between flex-wrap gap-2 mt-auto">
                  <span className="inline-block px-3 py-1 text-xs font-semibold text-cyan-300 bg-cyan-900/30 rounded-full border border-cyan-500/30">
                    {exp.period}
                  </span>
                  {exp.badge && (
                    <span className="inline-block px-2 py-1 text-xs font-medium text-emerald-300 bg-emerald-900/30 border border-emerald-500/30 rounded-full">
                                          <a
                      href={exp.companyUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs font-medium text-cyan-300 hover:text-cyan-200 transition-colors leading-tight"
                    >
                      {`VISIT ${exp.company}'S WEBSITE`}
                    </a>
                    </span>
                  )}
                </div>


                {/* Hover Effect Line */}
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-yellow-400 via-orange-400 to-red-400 rounded-b-2xl transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Experience;
